import { paymentGateways } from '@/utils/paymentMethods';
import {
	BaseAgreement,
} from '@/types/agreements';
import { Task } from '@/types/tasks';
import { getIsoDateString } from '@/utils/dates';
import {
	type ExchangeStatus,
	type LeaderboardStatus,
	LEADERBOARD_STATUSES,
	TRANSACTION_TASK_STATUSES,
	TRANSACTION_UNIT_TYPES,
	type TransactionPaymentType,
	type TransactionStatus,
	type TransactionType,
	type TransactionUnitType,
	type Sport,
	type ApiV1,
	type OnboardingStatus,
	type TransactionReportingMethod,
} from '@teamworksdev/influencer/core';
import { type MediaAttachment } from '@teamworksdev/vuetify';

type OnboardingStatusNullable = {
	stripe: OnboardingStatus['stripe'] | null;
	wallet: OnboardingStatus['wallet'] | null;
	isInternational: boolean;
	wallet_verification_status: OnboardingStatus['wallet_verification_status'] | null;
};

class Agent {
	firstName: string;
	lastName: string;
	fullName: string;
	email: string;
	phone: string;
	constructor(agent: ApiV1.Agent) {
		this.firstName = agent.first_name;
		this.lastName = agent.last_name;
		this.fullName = `${agent.first_name} ${agent.last_name}`;
		this.email = agent.email;
		this.phone = agent.phone;
	}
}

const isApiAgreementTransaction = (transaction: ApiV1.TransactionListTransaction | ApiV1.RecentTransaction | ApiV1.Transaction | ApiV1.AgreementTransaction): transaction is ApiV1.AgreementTransaction => {
	return 'amount' in transaction;
};

class Vendor {
	email: string;
	name: string;
	phone?: string | null;
	pointOfContact: string;

	constructor(vendor: ApiV1.Vendor) {
		this.email = vendor.email;
		this.name = vendor.name;
		this.phone = vendor.phone;
		this.pointOfContact = vendor.point_of_contact;
	}
}

class BaseTransaction {
	uuid: string;
	name: string;
	school: {
		uuid: string;
		name: string;
		showNilGoStatus?: boolean;
	} | null;

	cost: number;
	createdAt: string | null;
	updatedAt: string | null;
	isApprovalRequiredForPayment: boolean | null;
	athlete: { uuid: string; fullName: string } | null;
	exchangeStatus: ExchangeStatus | null;
	status: TransactionStatus | null;
	fees?: ApiV1.TransactionCostBreakdown | undefined;
	paymentGateway: typeof paymentGateways[number] | null;
	unitType: TransactionUnitType;
	leaderboardStatus?: LeaderboardStatus;
	leaderboardMultiplier?: number;
	usesPoints: boolean;
	totalPoints?: number;
	tasksOverview: ApiV1.TasksOverview;
	agreement: BaseAgreement | null = null;
	order: number | null = null;
	date: string | null = null;
	acceptedBy: string | null;
	acceptedAt: string | null;
	serviceProviderUsed?: boolean | null;
	schoolResourcesUsed?: boolean | null;
	reportingMethod: TransactionReportingMethod | null;
	onboardingStatus: OnboardingStatusNullable | null = null;
	preferredPayoutCustomerName: string | null = null;
	nilGo: ApiV1.TransactionNilGo | null = null;

	constructor(
		transaction:
			| ApiV1.TransactionListTransaction
			| ApiV1.RecentTransaction
			| ApiV1.Transaction
			| ApiV1.AgreementTransaction,
	) {
		this.name = transaction.name;
		this.uuid = transaction.uuid;
		this.cost = isApiAgreementTransaction(transaction) ? transaction.amount : transaction.cost;
		this.createdAt = 'created_at' in transaction ? transaction.created_at : null;
		this.updatedAt = 'updated_at' in transaction ? transaction.updated_at : null;
		this.exchangeStatus = transaction.exchange_status;
		this.reportingMethod = 'reporting_method' in transaction ? transaction.reporting_method : null;

		this.school = 'school' in transaction
			? {
				uuid: transaction.school.uuid,
				name: transaction.school.name,
				showNilGoStatus: transaction.school.show_nil_go_status,
			}
			: null;
		this.status = 'status' in transaction && !isApiAgreementTransaction(transaction) ? transaction.status : null;
		this.isApprovalRequiredForPayment = 'is_approval_required_for_payment' in transaction
			? transaction.is_approval_required_for_payment
			: isApiAgreementTransaction(transaction)
				? transaction.approval_required
				: null;
		this.unitType = 'unit_type' in transaction ? transaction.unit_type : TRANSACTION_UNIT_TYPES.US_DOLLARS;

		if ('athlete' in transaction) {
			this.athlete = transaction.athlete
				? {
					uuid: transaction.athlete.uuid,
					fullName: `${transaction.athlete.first_name} ${transaction.athlete.last_name}`,
				}
				: null;
		} else {
			this.athlete = null;
		}

		this.paymentGateway = 'payment_gateway' in transaction
			? paymentGateways.find((gateway) => gateway.id === transaction.payment_gateway) || null
			: null;

		if ('leaderboard' in transaction && transaction.leaderboard !== null) {
			this.leaderboardStatus = transaction.leaderboard.status;
			this.leaderboardMultiplier = transaction.leaderboard.multiplier;
		}

		this.usesPoints = this.unitType === TRANSACTION_UNIT_TYPES.POINTS;

		if (this.usesPoints) {
			this.totalPoints = this.cost * (this.leaderboardMultiplier || 1);
		}

		this.tasksOverview = {
			completed: 0,
			total: 0,
			percentage: null,
		};

		if ('agreement' in transaction && transaction.agreement !== null) {
			this.agreement = new BaseAgreement(transaction.agreement);
			this.order = transaction.display_order;
		}

		if ('display_order' in transaction) {
			this.order = transaction.display_order;
		}

		if ('accepted_at' in transaction) {
			this.acceptedAt = transaction.accepted_at;
		}

		if ('accepted_by' in transaction) {
			this.acceptedBy = transaction.accepted_by;
		}

		if ('date' in transaction) {
			this.date = transaction.date;
		} else if (isApiAgreementTransaction(transaction)) {
			this.date = getIsoDateString(transaction.due_at) ?? null;
		}

		if ('used_service_provider' in transaction) {
			this.serviceProviderUsed = transaction.used_service_provider;
		}

		if ('were_school_resources_used' in transaction) {
			this.schoolResourcesUsed = transaction.were_school_resources_used;
		}

		if ('nil_go' in transaction) {
			this.nilGo = transaction.nil_go;
		}
	}

	get effectiveLeaderboardStatus(): LeaderboardStatus {
		const status = this.leaderboardStatus ?? LEADERBOARD_STATUSES.NOT_READY_FOR_REVIEW;
		if (status === LEADERBOARD_STATUSES.READY_FOR_REVIEW
			&& this.tasksOverview.total > 0
			&& this.tasksOverview.completed < this.tasksOverview.total
		) {
			return LEADERBOARD_STATUSES.NOT_READY_FOR_REVIEW;
		}
		return status;
	}
}

class TransactionListTransaction extends BaseTransaction {
	date: string;
	vendor: Vendor;
	sport: Sport;
	deletedAt: string | null;
	constructor(transaction: ApiV1.TransactionListTransaction) {
		super(transaction);
		this.tasksOverview = transaction.tasks_overview;
		this.date = transaction.date;
		this.vendor = new Vendor(transaction.vendor);
		this.sport = transaction.sport_type;
		this.deletedAt = transaction.deleted_at;
	}
}

class Transaction extends BaseTransaction {
	id: number;
	attachments: ApiV1.Media[];
	athlete: { uuid: string; fullName: string; onboardingStatus: OnboardingStatus | null } | null;
	date: string;
	expirationDate?: string | null;
	multipleDates: boolean;
	multipleDatesText: string | null;
	notes: string | null;
	paymentType: TransactionPaymentType;
	transactionTypeId: TransactionType;
	tasks: Task[];
	vendor: Vendor;
	agent: Agent | null;
	auditor: ApiV1.Auditor | null;
	transactionFees: ApiV1.TransactionFee[];
	exchangeBrandUser: Pick<ApiV1.BaseUser, 'uuid'>;
	isGlobal: boolean | undefined;
	deletedAt: string | null;


	constructor(transaction: ApiV1.Transaction) {
		super(transaction);
		this.id = transaction.id;
		this.attachments = transaction.attachments;
		this.date = transaction.date;
		this.expirationDate = transaction.expiration_date;
		this.multipleDates = !!transaction.multiple_dates;
		this.multipleDatesText = transaction.multiple_dates_text;
		this.notes = transaction.notes;
		this.paymentType = transaction.payment_type;
		this.transactionTypeId = transaction.transaction_type;
		this.tasks = transaction.tasks.map((task) => new Task(task));
		if (transaction.tasks.length) {
			const completedTasks = transaction.tasks.filter((task) => task.status === TRANSACTION_TASK_STATUSES.COMPLETE).length;
			const totalTasks = transaction.tasks.length;
			this.tasksOverview = {
				completed: completedTasks,
				total: totalTasks,
				percentage: completedTasks / totalTasks,
			};
		}
		this.vendor = new Vendor(transaction.vendor);
		this.agent = transaction.agent && new Agent(transaction.agent);
		this.auditor = transaction.auditor;
		this.exchangeBrandUser = transaction.exchange_brand_user;

		this.transactionFees = transaction.transaction_fees;
		this.isGlobal = transaction.exchange_brand.is_global;
		if ('athlete' in transaction) {
			this.athlete = transaction.athlete
				? {
					uuid: transaction.athlete.uuid,
					fullName: `${transaction.athlete.first_name} ${transaction.athlete.last_name}`,
					onboardingStatus: transaction.athlete.onboarding_status ?? null,
				}
				: null;
		} else {
			this.athlete = null;
		}
		if ('deleted_at' in transaction) {
			this.deletedAt = transaction.deleted_at;
		}
	}
}

type TransactionFormTaskModel = ApiV1.TransactionTask & {
	taskUuid?: string;
	existingAttachments?: MediaAttachment[];
};

interface TransactionFormTaskUpdate {
	taskUuid: string;
	fields?: Pick<ApiV1.UpdateTaskModel, 'name' | 'description' | 'due_at'>;
	newAttachments?: ApiV1.MediaAttachment[];
	removedAttachmentIds?: string[];
}

export {
	Agent,
	type Vendor,
	type OnboardingStatusNullable,
	BaseTransaction,
	Transaction,
	TransactionListTransaction,
};

export type { TransactionFormTaskModel, TransactionFormTaskUpdate };
